import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/Toast';
import { colors, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const token = () => localStorage.getItem('access_token');
const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' });

// Maliyet uyarısı "olası veri hatası" eşiği: tek bir malzemenin birim fiyatı bu
// %'den fazla (mutlak) değişmişse genelde yanlış birim/miktar girişidir (örn.
// 3125 kg @ ₺5,16 yerine 250 kg @ ₺64,5 girilmesi → ~%92 düşüş). Gerçek zam/indirim
// nadiren bu seviyeyi aşar. Uyarıyı GİZLEMEZ (Anayasa M.6), sadece "kontrol et" diye işaretler.
const SUPHELI_FIYAT_DEGISIM_ESIK = 70;

// ─── Renk sabitleri ───
const KAT_RENK = { YILDIZ: '#28a745', BULMACA: '#007bff', SAVASCI: '#fd7e14', KOPEK: '#dc3545' };
const KAT_EMOJI = { YILDIZ: '⭐', BULMACA: '🧩', SAVASCI: '🐎', KOPEK: '🐕' };
const KAT_LABEL = { YILDIZ: 'Yıldız', BULMACA: 'Bulmaca', SAVASCI: 'Savaşçı', KOPEK: 'Köpek' };

// ─── POS Eşleştirme Uyarı Banner ───
const PosUyariBanner = ({ posEslestirme }) => {
  if (!posEslestirme || !posEslestirme.uyari) return null;
  return (
    <div style={{
      background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 10, padding: '14px 20px',
      marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12
    }}>
      <span style={{ fontSize: 22 }}>⚠️</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', fontSize: 14, color: '#856404', marginBottom: 2 }}>
          POS Ürün Eşleştirmesi Eksik
        </div>
        <div style={{ fontSize: 13, color: '#856404' }}>
          {posEslestirme.eslesmemis} POS ürünü henüz reçeteyle eşleşmemiş.
          Boston Matrisi'nde satış adetleri görünmeyecek ve popülerlik analizi yapılamayacaktır.
        </div>
      </div>
      <a href="/entegrasyon/eslestirme" style={{
        background: '#ffc107', color: '#856404', padding: '8px 16px', borderRadius: 6,
        textDecoration: 'none', fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap'
      }}>
        Eşleştirmeye Git
      </a>
    </div>
  );
};

// ─── Boston Matrisi SVG ───
const BostonMatrisi = ({ data, esikler }) => {
  const [tooltip, setTooltip] = useState(null);

  if (!data || data.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: colors.textTertiary }}>Analiz çalıştırın</div>;

  const W = 600, H = 400, PAD = 50;
  const maxBoyut = Math.max(...data.map(d => Math.abs(d.boyut)), 1);

  // Eksenler LOG ölçekli. Satış (0→~4000) ve katkı (₺34→₺5500) uç değerler içerir;
  // doğrusal ölçekte birkaç aykırı ürün (Kuzu Gerdan ₺5547, Çay 3999 satış) tüm
  // noktaları sol-alt köşeye sıkıştırıyordu (medyan ürün %0,5 / %5 konumunda).
  // log1p + her ekseni kendi min'inden normalize → bulk tüm tuvale yayılır,
  // aykırılar kenara, eşik çizgileri merkeze yakın. log monoton olduğu için
  // kadran üyeliği (eşiğin üstü/altı) korunur; negatif katkı 0'a kırpılır (dip).
  const _frac = (vals) => {
    const pos = vals.map(v => Math.max(v || 0, 0));
    const lo = Math.log1p(Math.min(...pos));
    const span = (Math.log1p(Math.max(...pos)) - lo) || 1;
    return (v) => Math.max(0, Math.min(1, (Math.log1p(Math.max(v || 0, 0)) - lo) / span));
  };
  const fx = _frac(data.map(d => d.x));
  const fy = _frac(data.map(d => d.y));

  const sx = (v) => PAD + fx(v) * (W - PAD * 2);
  const sy = (v) => H - PAD - fy(v) * (H - PAD * 2);

  const xLine = esikler ? sx(esikler.populerlik_esigi) : W / 2;
  const yLine = esikler ? sy(esikler.katki_payi_esigi) : H / 2;

  return (
    <div style={{ position: 'relative' }}>
      <svg width={W} height={H} style={{ background: '#fafafa', borderRadius: 8, border: `1px solid ${colors.border}` }}>
        {/* Kadran arka planları */}
        <rect x={xLine} y={PAD - 10} width={W - PAD - xLine} height={yLine - PAD + 10} fill="#e8f5e9" opacity={0.4} />
        <rect x={PAD} y={PAD - 10} width={xLine - PAD} height={yLine - PAD + 10} fill="#e3f2fd" opacity={0.4} />
        <rect x={xLine} y={yLine} width={W - PAD - xLine} height={H - PAD - yLine} fill="#fff3e0" opacity={0.4} />
        <rect x={PAD} y={yLine} width={xLine - PAD} height={H - PAD - yLine} fill="#ffebee" opacity={0.4} />

        {/* Kadran etiketleri */}
        <text x={xLine + (W - PAD - xLine) / 2} y={PAD + 14} textAnchor="middle" fill="#388e3c" fontSize={11} fontWeight="bold">⭐ YILDIZ</text>
        <text x={PAD + (xLine - PAD) / 2} y={PAD + 14} textAnchor="middle" fill="#1565c0" fontSize={11} fontWeight="bold">🧩 BULMACA</text>
        <text x={xLine + (W - PAD - xLine) / 2} y={H - PAD - 6} textAnchor="middle" fill="#e65100" fontSize={11} fontWeight="bold">🐎 SAVASCI</text>
        <text x={PAD + (xLine - PAD) / 2} y={H - PAD - 6} textAnchor="middle" fill="#c62828" fontSize={11} fontWeight="bold">🐕 KÖPEK</text>

        {/* Eşik çizgileri */}
        <line x1={xLine} y1={PAD - 10} x2={xLine} y2={H - PAD} stroke="#9e9e9e" strokeWidth={1} strokeDasharray="4,4" />
        <line x1={PAD} y1={yLine} x2={W - PAD} y2={yLine} stroke="#9e9e9e" strokeWidth={1} strokeDasharray="4,4" />

        {/* Eksen etiketleri */}
        <text x={W / 2} y={H - 8} textAnchor="middle" fill="#666" fontSize={11}>Satış Adedi (Popülerlik) — log ölçek →</text>
        <text x={12} y={H / 2} textAnchor="middle" fill="#666" fontSize={11} transform={`rotate(-90, 12, ${H / 2})`}>Katkı Payı (₺) — log ölçek →</text>

        {/* Ürün noktaları */}
        {data.map((d, i) => {
          const cx = sx(d.x);
          const cy = sy(d.y);
          const r = Math.max(6, Math.min(22, 6 + (Math.abs(d.boyut) / maxBoyut) * 16));
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill={KAT_RENK[d.kategori] || '#999'}
              opacity={0.75}
              stroke="#fff"
              strokeWidth={1.5}
              style={{ cursor: 'pointer', transition: 'r 0.2s' }}
              onMouseEnter={() => setTooltip({ ...d, cx, cy })}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute',
          left: tooltip.cx + 15,
          top: tooltip.cy - 10,
          background: '#333',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: 12,
          zIndex: 10,
          minWidth: 180,
          boxShadow: shadows.md
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{tooltip.urun}</div>
          <div>Satış: {tooltip.x} adet</div>
          <div>Katkı: ₺{tooltip.y?.toFixed(2)}</div>
          <div>Food Cost: %{tooltip.food_cost?.toFixed(1)}</div>
          <div>Kategori: {KAT_EMOJI[tooltip.kategori]} {KAT_LABEL[tooltip.kategori]}</div>
          {tooltip.trend && <div>Trend: {tooltip.trend === 'YUKSELIYOR' ? '↑' : tooltip.trend === 'DUSUYOR' ? '↓' : '→'}</div>}
        </div>
      )}
    </div>
  );
};

// ─── KPI Kart ───
const KpiCard = ({ baslik, deger, alt, renk = '#d32f2f', ikon }) => (
  <div style={{
    background: colors.surface, borderRadius: 10, padding: '18px 20px', flex: 1, minWidth: 200,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${renk}`
  }}>
    <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 6 }}>{ikon} {baslik}</div>
    <div style={{ fontSize: 24, fontWeight: 'bold', color: colors.textPrimary }}>{deger}</div>
    {alt && <div style={{ fontSize: 12, color: colors.textTertiary, marginTop: 4 }}>{alt}</div>}
  </div>
);

// ─── Kategori Özet Tablosu ───
const KategoriOzet = ({ ozet }) => {
  if (!ozet) return null;
  const rows = [
    { key: 'YILDIZ', count: ozet.yildiz_sayisi },
    { key: 'BULMACA', count: ozet.bulmaca_sayisi },
    { key: 'SAVASCI', count: ozet.savasci_sayisi },
    { key: 'KOPEK', count: ozet.kopek_sayisi },
  ];
  return (
    <div style={{ background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Kategori Dağılımı</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: colors.backgroundAlt }}>
            <th style={{ padding: 8, textAlign: 'left' }}>Kategori</th>
            <th style={{ padding: 8, textAlign: 'center' }}>Ürün</th>
            <th style={{ padding: 8, textAlign: 'center' }}>Oran</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: 8 }}>
                <span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                  background: KAT_RENK[r.key], marginRight: 8
                }} />
                {KAT_EMOJI[r.key]} {KAT_LABEL[r.key]}
              </td>
              <td style={{ padding: 8, textAlign: 'center', fontWeight: 'bold' }}>{r.count}</td>
              <td style={{ padding: 8, textAlign: 'center' }}>
                {ozet.toplam_urun > 0 ? `%${(r.count / ozet.toplam_urun * 100).toFixed(0)}` : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Uyarı Paneli ───
const UyariPaneli = ({ uyarilar, onOku }) => {
  if (!uyarilar || uyarilar.length === 0) return null;
  return (
    <div style={{ background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>⚠️ Maliyet Uyarıları</h3>
      {uyarilar.map(u => {
        const supheli = Math.abs(u.fiyat_degisim || 0) >= SUPHELI_FIYAT_DEGISIM_ESIK;
        return (
        <div key={u.id} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13
        }}>
          <div>
            <b>{u.malzeme}</b> fiyatı <span style={{ color: u.fiyat_degisim > 0 ? '#dc3545' : '#28a745' }}>
              %{u.fiyat_degisim > 0 ? '+' : ''}{u.fiyat_degisim?.toFixed(1)}
            </span>
            {' → '}<b>{u.urun}</b> maliyeti <span style={{ color: u.maliyet_degisim > 0 ? '#dc3545' : '#28a745' }}>
              %{u.maliyet_degisim > 0 ? '+' : ''}{u.maliyet_degisim?.toFixed(1)}
            </span>
            {supheli && (
              <a href="/recete-maliyet" title="Bu denli büyük bir fiyat değişimi genelde yanlış birim/miktar girişinden kaynaklanır. Fiyat girişini kontrol et." style={{
                display: 'inline-block', marginLeft: 8, background: '#fff3cd', color: '#856404',
                border: '1px solid #ffc107', borderRadius: 6, padding: '1px 7px', fontSize: 11,
                fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap'
              }}>⚠️ olası veri hatası</a>
            )}
          </div>
          <button onClick={() => onOku(u.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textTertiary, fontSize: 16
          }}>✕</button>
        </div>
        );
      })}
    </div>
  );
};

// ─── Açılır Öneri Hücresi ───
const OneriCell = ({ text }) => {
  const [acik, setAcik] = useState(false);
  if (!text) return <span style={{ color: colors.textTertiary }}>-</span>;

  // "|" ile ayrılmış önerileri satırlara böl
  const parcalar = text.split(' | ').filter(Boolean);
  const kisaMetin = parcalar[0] || '';
  const devami = parcalar.slice(1);

  return (
    <div style={{ fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ color: '#333' }}>{kisaMetin}</div>
      {devami.length > 0 && !acik && (
        <button onClick={() => setAcik(true)} style={{
          background: 'none', border: 'none', color: '#007bff', cursor: 'pointer',
          padding: '2px 0', fontSize: 12, fontWeight: 500
        }}>
          devamını oku ({devami.length} öneri daha)
        </button>
      )}
      {acik && devami.map((p, i) => (
        <div key={i} style={{
          color: p.startsWith('⚠️') ? '#856404' : '#555',
          background: p.startsWith('⚠️') ? '#fff3cd' : 'transparent',
          padding: p.startsWith('⚠️') ? '4px 8px' : 0,
          borderRadius: 4, marginTop: 4
        }}>
          {p}
        </div>
      ))}
      {acik && devami.length > 0 && (
        <button onClick={() => setAcik(false)} style={{
          background: 'none', border: 'none', color: colors.textTertiary, cursor: 'pointer',
          padding: '2px 0', fontSize: 11
        }}>
          kapat
        </button>
      )}
    </div>
  );
};

// ─── Food Cost Bilgi Tooltip ───
const FoodCostInfo = () => {
  const [goster, setGoster] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onClick={() => setGoster(p => !p)}
        style={{ cursor: 'pointer', color: colors.textTertiary, fontSize: 14, marginLeft: 6 }}
        title="Food Cost nasıl hesaplanır?"
      >ℹ️</span>
      {goster && (
        <div style={{
          position: 'absolute', top: 24, left: -200, width: 380, background: colors.surface,
          border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, zIndex: 100,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontSize: 13, lineHeight: 1.6
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>Food Cost Nasıl Hesaplanır?</div>
          <div style={{ marginBottom: 8 }}>
            <code style={{ background: colors.backgroundAlt, padding: '2px 6px', borderRadius: 4 }}>
              Food Cost % = (Porsiyon Maliyeti / Satış Fiyatı) × 100
            </code>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>Porsiyon Maliyeti</td>
                <td style={{ padding: '4px 0' }}>Reçetedeki tüm malzemelerin (birim fiyat × miktar) toplamı</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>Satış Fiyatı</td>
                <td style={{ padding: '4px 0' }}>Reçetedeki POS satış fiyatı (KDV dahil)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '4px 0', fontWeight: 'bold' }}>Katkı Payı</td>
                <td style={{ padding: '4px 0' }}>Satış Fiyatı - Porsiyon Maliyeti</td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: colors.textSecondary }}>
            <b>Sektör eşikleri:</b> %25 altı iyi, %25-35 kabul edilebilir, %35 üzeri yüksek
          </div>
          <button onClick={() => setGoster(false)} style={{
            position: 'absolute', top: 8, right: 10, background: 'none', border: 'none',
            cursor: 'pointer', fontSize: 16, color: colors.textTertiary
          }}>✕</button>
        </div>
      )}
    </span>
  );
};

// ─── Ürün Detay Tablosu (Tüm ürünler + öneriler) ───
const UrunDetayTablosu = ({ detaylar }) => {
  const [siralama, setSiralama] = useState('kategori');
  const [filtre, setFiltre] = useState('TUMU');

  if (!detaylar || detaylar.length === 0) return null;

  // Filtreleme
  let filtrelenmis = filtre === 'TUMU' ? detaylar : detaylar.filter(d => d.kategori === filtre);

  // Sıralama
  filtrelenmis = [...filtrelenmis].sort((a, b) => {
    if (siralama === 'kategori') {
      const s = { YILDIZ: 4, BULMACA: 3, SAVASCI: 2, KOPEK: 1 };
      return (s[b.kategori] || 0) - (s[a.kategori] || 0);
    }
    if (siralama === 'food_cost') return (a.food_cost || 0) - (b.food_cost || 0);
    if (siralama === 'katki_payi') return (b.katki_payi || 0) - (a.katki_payi || 0);
    if (siralama === 'satis_adedi') return (b.satis_adedi || 0) - (a.satis_adedi || 0);
    return 0;
  });

  const fcBar = (fc) => {
    const w = Math.min(fc, 60);
    const renk = fc <= 25 ? '#28a745' : fc <= 35 ? '#fd7e14' : '#dc3545';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 50, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${(w / 60) * 100}%`, height: '100%', background: renk, borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 12, color: renk, fontWeight: 'bold' }}>%{fc?.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div style={{ background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>
          📋 Ürün Detay Analizi <FoodCostInfo />
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Kategori filtre */}
          <div style={{ display: 'flex', gap: 4 }}>
            {['TUMU', 'YILDIZ', 'BULMACA', 'SAVASCI', 'KOPEK'].map(k => (
              <button key={k} onClick={() => setFiltre(k)} style={{
                padding: '4px 10px', borderRadius: 14, border: '1px solid',
                borderColor: filtre === k ? (KAT_RENK[k] || '#666') : '#ddd',
                background: filtre === k ? (KAT_RENK[k] || '#666') : '#fff',
                color: filtre === k ? '#fff' : '#666',
                cursor: 'pointer', fontSize: 12, fontWeight: filtre === k ? 'bold' : 'normal'
              }}>
                {k === 'TUMU' ? `Tümü (${detaylar.length})` : `${KAT_EMOJI[k]} ${detaylar.filter(d => d.kategori === k).length}`}
              </button>
            ))}
          </div>
          {/* Sıralama */}
          <select value={siralama} onChange={e => setSiralama(e.target.value)} style={{
            padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12
          }}>
            <option value="kategori">Kategoriye göre</option>
            <option value="food_cost">Food Cost (düşük → yüksek)</option>
            <option value="katki_payi">Katkı Payı (yüksek → düşük)</option>
            <option value="satis_adedi">Satış Adedi (yüksek → düşük)</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Ürün</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Satış ₺</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Maliyet ₺</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Food Cost</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Katkı ₺</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Satış Ad.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Kategori</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', minWidth: 250 }}>Öneri</th>
            </tr>
          </thead>
          <tbody>
            {filtrelenmis.map((d, i) => (
              <tr key={d.id || i} style={{
                borderBottom: '1px solid #f0f0f0',
                background: i % 2 === 0 ? '#fff' : '#fafafa'
              }}>
                <td style={{ padding: '10px 8px', fontWeight: 500 }}>
                  {d.urun}
                  {d.marka && <span style={{ fontSize: 10, color: colors.textTertiary, display: 'block' }}>{d.marka}</span>}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>₺{d.satis_fiyati?.toFixed(0)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>₺{d.porsiyon_maliyeti?.toFixed(0)}</td>
                <td style={{ padding: '10px 8px' }}>{fcBar(d.food_cost)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>₺{d.katki_payi?.toFixed(0)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', color: d.satis_adedi > 0 ? '#333' : '#ccc' }}>
                  {d.satis_adedi > 0 ? d.satis_adedi : <span title="POS eşleştirmesi yapılmamış">—</span>}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                    background: KAT_RENK[d.kategori] + '18',
                    color: KAT_RENK[d.kategori],
                    fontWeight: 'bold', fontSize: 12, whiteSpace: 'nowrap'
                  }}>
                    {KAT_EMOJI[d.kategori]} {KAT_LABEL[d.kategori]}
                  </span>
                  {d.trend && (
                    <div style={{ fontSize: 10, color: colors.textTertiary, marginTop: 2 }}>
                      {d.trend === 'YUKSELIYOR' ? '↑ Yükseliş' : d.trend === 'DUSUYOR' ? '↓ Düşüş' : '→ Stabil'}
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 8px' }}>
                  <OneriCell text={d.oneri} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtrelenmis.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', color: colors.textTertiary }}>
          Bu kategoride ürün yok
        </div>
      )}
    </div>
  );
};

// ─── Analiz sonuç paneli alt-liste (fiyatsız / maliyetsiz / haritalanmamış) ───
const SonucListesi = ({ baslik, renk, bg, items, render }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: bg, border: `1px solid ${renk}55`, borderRadius: 8, padding: '12px 14px', marginTop: 12 }}>
      <div style={{ fontWeight: 'bold', fontSize: 13, color: renk, marginBottom: 8 }}>{baslik} ({items.length})</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
        {items.map((it, i) => <span key={i} style={{ fontSize: 12, color: '#444' }}>{render(it)}</span>)}
      </div>
    </div>
  );
};

// ─── Analiz Sonuç Paneli (POST yanıtı: kapsam + eşik yöntemi + görünür uyarılar) ───
// ME-01 maliyet_eksik + ME-02 kapsam + ME-04 fiyatsız + ME-05 scope + ME-07 eşik yöntemi.
const AnalizSonucPanel = ({ sonuc, onClose }) => {
  if (!sonuc) return null;
  const kapsam = sonuc.kapsam || {};
  const fiyatsiz = sonuc.fiyatsiz_urunler || [];
  const maliyetsiz = sonuc.maliyet_eksik_urunler || [];
  const haritalanmamis = sonuc.haritalanmamis_urunler || [];
  const esikYontemi = sonuc.esik_yontemi === 'medyan' ? 'Medyan' : 'Ortalama';
  const fmt = (v) => `₺${Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  const lokLabel = kapsam.lokasyon_kapsami === 'TEK_LOKASYON'
    ? `Tek lokasyon (#${kapsam.lokasyon_id})` : 'Tüm lokasyonlar';
  const temiz = fiyatsiz.length === 0 && maliyetsiz.length === 0;

  return (
    <div style={{
      background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10,
      padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>📋 Son Analiz Sonucu</h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: colors.textTertiary, fontSize: 18
        }} title="Paneli kapat">✕</button>
      </div>

      {/* Kapsam + eşik yöntemi şeridi */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
        <span style={{ fontSize: 12, background: colors.backgroundAlt, borderRadius: 14, padding: '4px 12px' }}>
          📊 Kapsam: <b>%{kapsam.kapsam_pct ?? 0}</b>
          {kapsam.kapsam_pct_yaklasik && <span style={{ color: '#b8860b' }}> (yaklaşık)</span>}
          {' '}({fmt(kapsam.recipe_bagli_ciro)} / {fmt(kapsam.toplam_satis_ciro)})
        </span>
        <span style={{ fontSize: 12, background: colors.backgroundAlt, borderRadius: 14, padding: '4px 12px' }}>
          🎯 Kapsam alanı: <b>{lokLabel}</b>{kapsam.marka ? ` · Marka: ${kapsam.marka_ad || kapsam.marka}` : ''}
        </span>
        <span style={{ fontSize: 12, background: '#e8f0fe', borderRadius: 14, padding: '4px 12px' }}>
          ⚖️ Eşik yöntemi: <b>{esikYontemi}</b>
        </span>
      </div>

      {kapsam.uyari && (
        <div style={{
          background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8,
          padding: '10px 14px', marginTop: 12, fontSize: 12, color: '#856404'
        }}>
          ⚠️ {kapsam.uyari}
        </div>
      )}

      {/* ME-04: Fiyatsız tabaklar */}
      <SonucListesi
        baslik="🏷️ Fiyatı tanımsız tabaklar (analize girmedi)"
        renk="#dc3545" bg="#fdecea" items={fiyatsiz}
        render={(it) => it.urun}
      />
      {fiyatsiz.length > 0 && (
        <a href="/recete-maliyet" style={{
          display: 'inline-block', marginTop: 8, fontSize: 12, color: '#dc3545', fontWeight: 600
        }}>→ Reçete &amp; Maliyet'te POS satış fiyatı gir</a>
      )}

      {/* ME-01: Maliyetsiz tabaklar */}
      <SonucListesi
        baslik="🧮 Maliyeti hesaplanamayan tabaklar (analize girmedi)"
        renk="#fd7e14" bg="#fff4e8" items={maliyetsiz}
        render={(it) => it.urun}
      />

      {/* ME-02: Haritalanmamış POS ürünleri */}
      <SonucListesi
        baslik="🔌 Reçeteye bağlanmamış POS ürünleri (yüksek ciro)"
        renk="#6c757d" bg="#f1f3f5" items={haritalanmamis}
        render={(it) => `${it.urun} (${fmt(it.ciro)})`}
      />

      {temiz && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#28a745' }}>
          ✓ Tüm tabakların fiyatı ve maliyeti tanımlı — eksik uyarı yok.
        </div>
      )}
    </div>
  );
};

// ─── Ana Sayfa ───
export default function MenuMuhendisligiPage() {
  const toast = useToast();
  const [dashboard, setDashboard] = useState(null);
  const [matris, setMatris] = useState(null);
  const [markalar, setMarkalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analizModal, setAnalizModal] = useState(false);
  const [analizSonuc, setAnalizSonuc] = useState(null);
  const [analizForm, setAnalizForm] = useState({
    baslangic_tarihi: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0],
    bitis_tarihi: new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString().split('T')[0],
    lokasyon_id: null,
    marka_id: null
  });
  const [analizLoading, setAnalizLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/menu-muhendisligi/dashboard`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);

        // Son analizin matris verisini yükle
        if (data.son_analiz?.analiz_id) {
          const mRes = await fetch(`${API}/api/menu-muhendisligi/analiz/${data.son_analiz.analiz_id}/matris`, { headers: headers() });
          if (mRes.ok) setMatris(await mRes.json());
        }
      }

      // Marka listesini DB'den yükle (marka filtresi için). ME-08: reçeteler marka_id
      // (FK → markalar) ile etiketli; firmalar tablosu/kod DEĞİL.
      const mkRes = await fetch(`${API}/api/markalar`, { headers: headers() });
      if (mkRes.ok) setMarkalar(await mkRes.json());
    } catch (err) { logError('Dashboard yüklenemedi:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const analizCalistir = async () => {
    setAnalizLoading(true);
    try {
      const res = await fetch(`${API}/api/menu-muhendisligi/analiz`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(analizForm)
      });
      if (res.ok) {
        const data = await res.json();
        setAnalizSonuc(data);  // POST yanıtını yakala → kapsam/fiyatsız/eşik yöntemi paneli
        setAnalizModal(false);
        loadDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Analiz çalıştırılamadı');
      }
    } catch (err) { toast.error('Hata: ' + err.message); }
    setAnalizLoading(false);
  };

  const uyariOku = async (id) => {
    await fetch(`${API}/api/menu-muhendisligi/uyarilar/${id}/oku`, { method: 'PATCH', headers: headers() });
    loadDashboard();
  };

  if (loading && !dashboard && !matris) return <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>;

  const ozet = dashboard?.son_analiz?.ozet;
  const detaylar = dashboard?.son_analiz?.detaylar;
  const fcRenk = ozet ? (ozet.ortalama_food_cost < 30 ? '#28a745' : ozet.ortalama_food_cost > 35 ? '#dc3545' : '#fd7e14') : '#999';

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400 }}>
      {/* Başlık */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: colors.textPrimary }}>📊 Menü Mühendisliği</h1>
          <p style={{ margin: '4px 0 0', color: colors.textTertiary, fontSize: 13 }}>
            Boston Matrisi ile menü karlılık analizi
            {dashboard?.son_analiz?.donem && (
              <span style={{ marginLeft: 8, color: colors.textSecondary }}>| Son analiz: {dashboard.son_analiz.donem}</span>
            )}
          </p>
        </div>
        <button onClick={() => setAnalizModal(true)} style={{
          background: '#d32f2f', color: '#fff', border: 'none', padding: '10px 20px',
          borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 14
        }}>
          🔬 Yeni Analiz Çalıştır
        </button>
      </div>

      {/* POS Eşleştirme Uyarısı */}
      <PosUyariBanner posEslestirme={dashboard?.pos_eslestirme} />

      {/* Analiz sonuç paneli (yeni analiz sonrası: kapsam + fiyatsız/maliyetsiz + eşik yöntemi) */}
      <AnalizSonucPanel sonuc={analizSonuc} onClose={() => setAnalizSonuc(null)} />

      {/* KPI Kartları */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <KpiCard
          baslik="Ort. Food Cost"
          deger={ozet ? `%${ozet.ortalama_food_cost?.toFixed(1)}` : '-'}
          alt={ozet ? `${ozet.toplam_urun} ürün analiz edildi` : 'Henüz analiz yok'}
          renk={fcRenk}
          ikon="🍽️"
        />
        <KpiCard
          baslik="Toplam Katkı"
          deger={ozet ? `₺${ozet.toplam_katki?.toLocaleString('tr-TR', { minimumFractionDigits: 0 })}` : '-'}
          alt={ozet ? `Dönem cirosu: ₺${ozet.toplam_ciro?.toLocaleString('tr-TR')}` : ''}
          renk="#28a745"
          ikon="💰"
        />
        <KpiCard
          baslik="Yıldız Ürünler"
          deger={ozet ? `${ozet.yildiz_sayisi} / ${ozet.toplam_urun}` : '-'}
          alt="Karlı ve popüler"
          renk="#28a745"
          ikon="⭐"
        />
        <KpiCard
          baslik="Maliyet Uyarısı"
          deger={dashboard?.uyari_sayisi || 0}
          alt="Okunmamış uyarı"
          renk={dashboard?.uyari_sayisi > 0 ? '#dc3545' : '#28a745'}
          ikon="⚠️"
        />
      </div>

      {/* Boston Matrisi + Kategori Özet yan yana */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 400, background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Boston Matrisi</h3>
          <BostonMatrisi data={matris?.noktalar} esikler={matris?.esikler} />
        </div>
        <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <KategoriOzet ozet={ozet} />
          <UyariPaneli uyarilar={dashboard?.son_uyarilar} onOku={uyariOku} />
        </div>
      </div>

      {/* En Karlı ve En Çok Satan */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 350, background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>💰 En Karlı 5 Ürün</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: colors.backgroundAlt }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Ürün</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Katkı Payı</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Food Cost</th>
            </tr></thead>
            <tbody>
              {(dashboard?.en_karli_5 || []).map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: 8 }}>{KAT_EMOJI[u.kategori]} {u.urun}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 'bold' }}>₺{u.katki_payi?.toFixed(2)}</td>
                  <td style={{ padding: 8, textAlign: 'right', color: u.food_cost > 35 ? '#dc3545' : '#28a745' }}>%{u.food_cost?.toFixed(1)}</td>
                </tr>
              ))}
              {(!dashboard?.en_karli_5 || dashboard.en_karli_5.length === 0) && (
                <tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: colors.textTertiary }}>Analiz çalıştırın</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ flex: 1, minWidth: 350, background: colors.surface, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>🔥 En Çok Satan 5 Ürün</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: colors.backgroundAlt }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Ürün</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Satış</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Toplam Katkı</th>
            </tr></thead>
            <tbody>
              {(dashboard?.en_cok_satan_5 || []).map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: 8 }}>{KAT_EMOJI[u.kategori]} {u.urun}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 'bold' }}>{u.satis_adedi} adet</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>₺{u.toplam_katki?.toFixed(0)}</td>
                </tr>
              ))}
              {(!dashboard?.en_cok_satan_5 || dashboard.en_cok_satan_5.length === 0) && (
                <tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: colors.textTertiary }}>Analiz çalıştırın</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ürün Detay Tablosu */}
      <UrunDetayTablosu detaylar={detaylar} />

      {/* Analiz Çalıştır Modal */}
      {analizModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }} onClick={() => setAnalizModal(false)}>
          <div style={{
            background: colors.surface, borderRadius: 12, padding: 28, width: 420,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>🔬 Yeni Analiz Çalıştır</h2>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Başlangıç Tarihi</label>
              <input type="date" value={analizForm.baslangic_tarihi}
                onChange={e => setAnalizForm(p => ({ ...p, baslangic_tarihi: e.target.value }))}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Bitiş Tarihi</label>
              <input type="date" value={analizForm.bitis_tarihi}
                onChange={e => setAnalizForm(p => ({ ...p, bitis_tarihi: e.target.value }))}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Marka (opsiyonel)</label>
              <select value={analizForm.marka_id || ''} onChange={e => setAnalizForm(p => ({ ...p, marka_id: e.target.value ? Number(e.target.value) : null }))}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }}>
                <option value="">Tümü</option>
                {markalar.filter(m => m.aktif !== false).map(m => (
                  <option key={m.id} value={m.id}>{m.ad}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setAnalizModal(false)} style={{
                padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.surface, cursor: 'pointer'
              }}>İptal</button>
              <button onClick={analizCalistir} disabled={analizLoading} style={{
                padding: '8px 20px', borderRadius: 6, border: 'none', background: '#d32f2f',
                color: '#fff', cursor: 'pointer', fontWeight: 'bold'
              }}>
                {analizLoading ? 'Çalışıyor...' : 'Analizi Başlat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
